import React, { useEffect, useMemo, useState, useCallback } from "react";
import BrandLoader from '../shared/brand/BrandLoader';
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useTranslation } from 'react-i18next';
import EnhancedAnalyticsCharts, { ChartDataInterface } from "./EnhancedAnalyticsCharts";
import { getDemoAnalytics } from "./demoAnalyticsData";
import ColorCustomizer, { getStoredTheme, setStoredTheme, getStoredChartType, setStoredChartType, ChartTypeOverride } from "./ColorCustomizer";
import MetricCard from "./MetricCard";
import Badge from "../shared/TailwindComponents/Badge";
import FilterBarWithTabs from "../shared/TailwindComponents/FilterBarWithTabs";
import Header from "../shared/TailwindComponents/Header";
import {
  PiFileTextLight,
  PiCurrencyDollarLight,
  PiLightningLight,
  PiUsersLight,
  PiBriefcaseLight,
  PiBuildingsLight,
  PiCalendarBlankLight,
  PiFunnelLight,
  PiCloudArrowDownLight,
  PiCheckSquareOffsetLight,
  PiCalendarLight,
  PiDatabaseLight,
  PiTableLight,
  PiChatsCircleLight,
  PiMagnifyingGlassLight,
  PiArticleLight,
  PiEyeLight,
  PiHardDrivesLight,
  PiClockCountdownLight,
  PiChatTextLight,
  PiShieldCheckLight,
  PiScalesLight,
  PiMapPinLight,
  PiWarningCircleLight,
  PiTimerLight,
  PiUsersThreeLight,
  PiPaperclipLight,
  PiChatDotsLight,
  PiReceiptLight,
  PiArrowsClockwiseLight,
  PiQuestionLight,
  PiTargetLight,
  PiChartBarLight,
  PiGaugeLight,
} from 'react-icons/pi';
import apis from "../../utils/apis";
import Dropdown from "../shared/TailwindComponents/Dropdown";
import CreateReportModal from "./CreateReportModal";
import AnalyticsHelpModal from "./AnalyticsHelpModal";
import Skeleton from '../shared/TailwindComponents/Skeleton';


interface EnhancedReportsPageProps {}

const EnhancedReportsPage: React.FC<EnhancedReportsPageProps> = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>("personal");
  const [chartData, setChartData] = useState<ChartDataInterface[]>([]);
  const [shortChartData, setShortChartData] = useState<ChartDataInterface[]>([]);
  const [barChartData, setBarChartData] = useState<ChartDataInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [analyticsSummary, setAnalyticsSummary] = useState<
    { key: string; label: string; total: number; trend?: { trend_percent: number; trend_status: string }; color?: string; gradient?: string }[]
  >([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>("last-30-days");
  const [customDateFrom, setCustomDateFrom] = useState<string>("");
  const [customDateTo, setCustomDateTo] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("all");
  const [colorTheme, setColorTheme] = useState(() => getStoredTheme());
  const [chartType, setChartType] = useState<ChartTypeOverride>(() => getStoredChartType());
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const sectionTabs = useMemo(() => {
    const base = [
      { value: "all", label: t("All") },
      ...(activeTab === "organization" ? [{ value: "executive", label: t("Executive") }] : []),
      { value: "overview", label: t("Overview") },
      { value: "activity-trends", label: t("Activity Trends") },
      { value: "quick-insights", label: t("Quick Insights") },
      { value: "detailed-analytics", label: t("Detailed Analytics") },
    ];
    return base;
  }, [activeTab]);

  const tabs = [
    { label: t("Personal"), value: "personal", icon: <PiUsersLight className="w-4 h-4" /> },
    ...(userInfo?.is_org_admin
      ? [{ label: t("Organization"), value: "organization", icon: <PiBuildingsLight className="w-4 h-4" /> }]
      : []),
  ];

  const getFilterOptions = useMemo(() => {
    const personal = [
      "Matters", "Tasks", "Projects", "Reviews", "Clients", "Research", "Drafts",
      "Files", "Templates", "Data Sources", "Tables",
    ];
    const orgExtra = ["People", "Chat", "Executive"];
    switch (activeTab) {
      case "personal":
        return personal;
      case "organization":
        return [...personal, ...orgExtra];
      default:
        return personal;
    }
  }, [activeTab]);


  const fetchData = async () => {
    setLoading(true);
    try {
      // Dev/demo only: render illustrative analytics for screenshots without hitting the API.
      // Enable with REACT_APP_ANALYTICS_DEMO=true, or set window.__PONS_DEMO_MODE = true in the
      // browser console and change the time range to refetch. Inert in all normal runs.
      const demoOn =
        process.env.REACT_APP_ANALYTICS_DEMO === "true" ||
        (typeof window !== "undefined" && (window as any).__PONS_DEMO_MODE === true);
      if (demoOn) {
        const demo = getDemoAnalytics(activeTab);
        setAnalyticsSummary(demo.static_fields as any);
        setBarChartData(demo.barChartData);
        setShortChartData(demo.shortChartData);
        setChartData(demo.chartData);
        return;
      }

      const endpoint = activeTab === "organization"
        ? "/analytics/organization"
        : "/analytics/personal";

      const params: any = {
        preset: selectedTimeRange,
      };

      if (selectedTimeRange === 'custom' && customDateFrom && customDateTo) {
        params.date_from = customDateFrom;
        params.date_to = customDateTo;
      }

      // Send category filters (multi-select – skip when empty / "all")
      if (activeSection === "executive") {
        // Executive section tab → request only executive analytics
        params["filters[]"] = ["executive"];
      } else if (selectedFilters.length > 0) {
        params["filters[]"] = selectedFilters.map((f) => f.replace(/-/g, "_"));
      }

      // Always request daily granularity so H/D/W/M client-side rebucketing works
      params.granularity = 'days';

      const response = await apis.get(endpoint, { params });
      if (!response || !response.data) throw new Error("Failed to fetch analytics data");

      const { chartData: cd = [], static_fields = [], shortChartData: scd = [], barChartData: bcd = [] } = response.data;
      setChartData(cd);
      setShortChartData(scd);
      setAnalyticsSummary(static_fields);
      setBarChartData(bcd);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setChartData([]);
      setShortChartData([]);
      setBarChartData([]);
      setAnalyticsSummary([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTimeRange === 'custom') {
      if (customDateFrom && customDateTo) {
        fetchData();
      }
    } else {
      fetchData();
    }
  }, [selectedTimeRange, customDateFrom, customDateTo, selectedFilters, activeTab, activeSection]);

  // Reset section tab when switching between personal / organization
  useEffect(() => {
    if (activeTab === "personal" && activeSection === "executive") {
      setActiveSection("all");
    }
  }, [activeTab]);

  const getMetricIcon = (field: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      // Core entities
      matters: <PiBriefcaseLight className="w-5 h-5" />,
      tasks: <PiCheckSquareOffsetLight className="w-5 h-5" />,
      overdue_tasks: <PiClockCountdownLight className="w-5 h-5" />,
      clients: <PiUsersLight className="w-5 h-5" />,
      research_queries: <PiMagnifyingGlassLight className="w-5 h-5" />,
      tokens_used: <PiLightningLight className="w-5 h-5" />,
      citations: <PiArticleLight className="w-5 h-5" />,
      drafts: <PiFileTextLight className="w-5 h-5" />,
      insights: <PiEyeLight className="w-5 h-5" />,
      templates: <PiArticleLight className="w-5 h-5" />,
      ai_processed: <PiLightningLight className="w-5 h-5" />,
      revenue: <PiCurrencyDollarLight className="w-5 h-5" />,
      // Files & storage
      files_uploaded: <PiFileTextLight className="w-5 h-5" />,
      storage_used: <PiHardDrivesLight className="w-5 h-5" />,
      // Data Sources / Tables
      knowledge_bases: <PiDatabaseLight className="w-5 h-5" />,
      indexed_documents: <PiHardDrivesLight className="w-5 h-5" />,
      tables: <PiTableLight className="w-5 h-5" />,
      documents_analyzed: <PiTableLight className="w-5 h-5" />,
      // Org / Users
      team_members: <PiUsersLight className="w-5 h-5" />,
      active_members: <PiUsersLight className="w-5 h-5" />,
      "active_sessions_(24h)": <PiChatsCircleLight className="w-5 h-5" />,
      chats: <PiChatTextLight className="w-5 h-5" />,
      messages: <PiChatTextLight className="w-5 h-5" />,
      // Legal / enriched analytics
      overdue_matters: <PiWarningCircleLight className="w-5 h-5" />,
      confidential: <PiShieldCheckLight className="w-5 h-5" />,
      avg_baseline_creation: <PiTimerLight className="w-5 h-5" />,
      avg_matter_duration: <PiTimerLight className="w-5 h-5" />,
      avg_turnaround: <PiArrowsClockwiseLight className="w-5 h-5" />,
      avg_research_response: <PiClockCountdownLight className="w-5 h-5" />,
      "avg_matters/client": <PiBriefcaseLight className="w-5 h-5" />,
      // Executive / Tier-1 metrics
      clients_per_lawyer: <PiScalesLight className="w-5 h-5" />,
      tokens_per_citation: <PiLightningLight className="w-5 h-5" />,
      stale_data_sources: <PiClockCountdownLight className="w-5 h-5" />,
      seats_used: <PiReceiptLight className="w-5 h-5" />,
    };
    const key = field.toLowerCase();
    return iconMap[key] || <PiBuildingsLight className="w-5 h-5" />;
  };

  const handleColorThemeChange = useCallback((themeKey: string) => {
    setColorTheme(themeKey);
    setStoredTheme(themeKey);
  }, []);

  const formatMetricTitle = (field: string) => {
    return field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char: string) => char.toUpperCase());
  };

  const formatMetricValue = (field: string, count: number | string) => {
    if (typeof count === 'string') return count;
    if (field === 'Revenue' || field === 'Unbillable Hours') {
      return `$${count.toLocaleString()}`;
    }
    return count.toLocaleString();
  };

  const handleGenerateReport = async (config?: any) => {
    setIsLoading(true);
    setReportModalOpen(false);
    try {
      const payload = {
        title: config?.title || t("Analytics Report"),
        description: config?.description || "",
        timeRange: config?.timeRange || selectedTimeRange,
        customDateFrom: config?.customDateFrom || customDateFrom,
        customDateTo: config?.customDateTo || customDateTo,
        categories: config?.categories || (selectedFilters.length === 0 ? getFilterOptions.map(f => f.toLowerCase().replace(" ", "-")) : selectedFilters),
        sections: config?.sections || [],
        orientation: config?.orientation || "portrait",
        includeComparison: config?.includeComparison || false,
        comparisonPeriod: config?.comparisonPeriod || "previous",
        includeHeader: config?.includeHeader ?? true,
        includeFooter: config?.includeFooter ?? true,
        groupBy: config?.groupBy || "category",
        scope: activeTab,
      };

      const response = await apis.post("/analytics/report", payload, {
        responseType: "blob",
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `analytics_report_${payload.title.replace(/\s+/g, "_").toLowerCase()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to generate report");
      }
    } catch (error) {
      console.error("Report error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const actions = [
    {
      label: isLoading ? t("Generating...") : t("Create Report"),
      onClick: () => setReportModalOpen(true),
      variant: 'primary' as const,
      icon: isLoading ? <BrandLoader size="xs" className="mr-2" /> : <PiCloudArrowDownLight className="w-4 h-4 mr-2" />,
      disabled: isLoading,
    },
    {
      label: "",
      onClick: () => setHelpModalOpen(true),
      variant: 'outline' as const,
      icon: <PiQuestionLight className="w-4 h-4" />,
      disabled: false,
    },
  ];


  const timeRangeOptions = [
    { value: "last-7-days", label: t("Last 7 days") },
    { value: "last-30-days", label: t("Last 30 days") },
    { value: "last-90-days", label: t("Last 90 days") },
    { value: "last-year", label: t("Last year") },
    { value: "custom", label: t("Custom range") },
  ];

  const filterOptions = [
    ...getFilterOptions.map((option) => ({
      value: option.toLowerCase().replace(" ", "-"),
      label: option,
    })),
  ];

  const additionalRightElements = (
    <>
      <Dropdown
        options={timeRangeOptions}
        value={selectedTimeRange}
        onChange={(val) => setSelectedTimeRange(val as string)}
        className="!bg-surface dark:!bg-gray-800 border-slate-200 hover:bg-gray-50 transition-all duration-200 !py-1.5 !min-w-[160px]"
        leftIcon={<PiCalendarLight className="w-4 h-4 text-slate-500" />}
        popupMinWidth={200}
        width="w-auto"
      />
      {selectedTimeRange === 'custom' && (
        <>
          <input
            type="date"
            value={customDateFrom}
            onChange={(e) => setCustomDateFrom(e.target.value)}
            className="px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
          <span className="text-xs text-gray-400">–</span>
          <input
            type="date"
            value={customDateTo}
            onChange={(e) => setCustomDateTo(e.target.value)}
            className="px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
        </>
      )}
      <Dropdown
        options={filterOptions}
        value={selectedFilters}
        onChange={(val) => setSelectedFilters(Array.isArray(val) ? val : val ? [val] : [])}
        placeholder={t('Filter by...')}
        className="!bg-surface dark:!bg-gray-800 border-slate-200 hover:bg-gray-50 transition-all duration-200 !py-1.5 !min-w-[160px]"
        leftIcon={<PiFunnelLight className="w-4 h-4 text-slate-500" />}
        popupMinWidth={200}
        popupMaxHeight={500}
        width="w-auto"
        multiple
      />
      <ColorCustomizer
        activeTheme={colorTheme}
        onThemeChange={handleColorThemeChange}
        chartType={chartType}
        onChartTypeChange={(t) => { setChartType(t); setStoredChartType(t); }}
      />
    </>
  );



  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
      <div className="flex-shrink-0">
        <Header actions={actions} />
      </div>
      <div className="flex-shrink-0 bg-background-light dark:bg-background-dark border-b border-gray-200 dark:border-gray-700">
        <div className="w-full mx-auto px-4 pt-2 z-[5000000]">
          <FilterBarWithTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={setActiveTab}
            noParentHeight
            noParentPadding
            noBorder
            additionalControls={additionalRightElements}
          />
        </div>
      </div>

      {/* Secondary navigation bar — same style as FilterBarWithTabs */}
      <div className="flex-shrink-0 bg-background-light dark:bg-background-dark border-b border-gray-200 dark:border-gray-700">
        <div className="w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Section tabs */}
            <div className="flex items-center gap-2">
              {sectionTabs.map((tab) => {
                const isActive = activeSection === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveSection(tab.value)}
                    className={`flex items-center px-3 py-1 text-xs font-medium rounded nav-pill ${
                      isActive
                        ? 'nav-pill-active text-primary-600 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {t(tab.label)}
                  </button>
                );
              })}
            </div>

            {/* Active Filters — right side */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">{t('Active Filters')}</span>
              <Badge className="px-3 py-1 bg-info-100/70 text-info-700 border-info-200 dark:bg-info-900/20 dark:text-info-300 dark:border-info-800">
                <PiBriefcaseLight className="w-3 h-3 mr-1" />
                {t(activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' '))}
              </Badge>
              {selectedTimeRange && (
                <Badge className="px-3 py-1 bg-white/70 border-gray-200 dark:bg-gray-800/70 dark:border-gray-700">
                  <PiCalendarBlankLight className="w-3 h-3 mr-1" />
                  {selectedTimeRange === 'custom' && customDateFrom && customDateTo
                    ? t(`${customDateFrom} – ${customDateTo}`)
                    : t(selectedTimeRange.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))}
                </Badge>
              )}
              {selectedFilters.length > 0 && selectedFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setSelectedFilters((prev) => prev.filter((v) => v !== f))}
                  className="inline-flex items-center px-3 py-1 text-xs rounded-full border bg-white/70 border-gray-200 dark:bg-gray-800/70 dark:border-gray-700 cursor-pointer hover:bg-danger-50 hover:border-danger-200 transition-colors text-gray-700 dark:text-gray-300"
                >
                  <PiFunnelLight className="w-3 h-3 mr-1" />
                  {t(f.charAt(0).toUpperCase() + f.slice(1).replace(/-/g, ' '))}
                  <span className="ml-1 text-gray-400 hover:text-danger-500">&times;</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
      <div className="w-full mx-auto px-6 py-8">
        {loading ? (
          <Skeleton variant="grid" rows={6} />
        ) : (
          <div className="space-y-8 animate-fade-in">
            {/* Overview — Summary Cards */}
            {(activeSection === "all" || activeSection === "overview") && (
              <div>
                {activeSection === "all" && (
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Overview')}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('Summary of key metrics')}</p>
                  </div>
                )}
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${analyticsSummary.length <= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-6`}>
                  {analyticsSummary.map((item: any, index) => (
                    <MetricCard
                      key={item.key}
                      title={formatMetricTitle(item.label)}
                      value={formatMetricValue(item.key, item.total)}
                      icon={getMetricIcon(item.key)}
                      change={item.trend && {
                        value: item.trend.trend_percent,
                        type: item.trend.trend_status || 'neutral',
                      }}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Activity Trends */}
            {(activeSection === "all" || activeSection === "activity-trends") && barChartData.length > 0 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Activity Trends')}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('How your activity has progressed over time')}</p>
                </div>
                <EnhancedAnalyticsCharts
                  chartsData={barChartData}
                  isLoading={false}
                  seperatedChart={true}
                  colorTheme={colorTheme}
                  chartTypeOverride={chartType}
                />
              </div>
            )}
            {activeSection === "activity-trends" && barChartData.length === 0 && (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <p className="text-sm">{t('No activity trend data available for this period.')}</p>
              </div>
            )}

            {/* Quick Insights */}
            {(activeSection === "all" || activeSection === "quick-insights") && shortChartData.length > 0 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Quick Insights')}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('Key performance indicators at a glance')}</p>
                </div>
                <EnhancedAnalyticsCharts
                  chartsData={shortChartData}
                  isLoading={false}
                  colorTheme={colorTheme}
                  chartTypeOverride={chartType}
                />
              </div>
            )}
            {activeSection === "quick-insights" && shortChartData.length === 0 && (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <p className="text-sm">{t('No insight data available for this period.')}</p>
              </div>
            )}

            {/* Detailed Analytics */}
            {(activeSection === "all" || activeSection === "detailed-analytics") && chartData.length > 0 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Detailed Analytics')}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('Comprehensive view of your business metrics')}</p>
                </div>
                <EnhancedAnalyticsCharts
                  chartsData={chartData}
                  isLoading={false}
                  colorTheme={colorTheme}
                  chartTypeOverride={chartType}
                />
              </div>
            )}
            {activeSection === "detailed-analytics" && chartData.length === 0 && (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <p className="text-sm">{t('No detailed analytics data available for this period.')}</p>
              </div>
            )}

            {/* Executive — shows all data sections when selected */}
            {activeSection === "executive" && (
              <>
                {analyticsSummary.length > 0 && (
                  <div>
                    <div className="mb-4">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Executive overview')}</h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('Organization-wide key metrics')}</p>
                    </div>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${analyticsSummary.length <= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-6`}>
                      {analyticsSummary.map((item: any, index) => (
                        <MetricCard
                          key={item.key}
                          title={formatMetricTitle(item.label)}
                          value={formatMetricValue(item.key, item.total)}
                          icon={getMetricIcon(item.key)}
                          change={item.trend && {
                            value: item.trend.trend_percent,
                            type: item.trend.trend_status || 'neutral',
                          }}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {shortChartData.length > 0 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Gauges & snapshots')}</h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('Quick performance indicators')}</p>
                    </div>
                    <EnhancedAnalyticsCharts chartsData={shortChartData} isLoading={false} colorTheme={colorTheme} chartTypeOverride={chartType} />
                  </div>
                )}
                {barChartData.length > 0 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Trends')}</h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('Activity over your selected period')}</p>
                    </div>
                    <EnhancedAnalyticsCharts chartsData={barChartData} isLoading={false} seperatedChart={true} colorTheme={colorTheme} chartTypeOverride={chartType} />
                  </div>
                )}
                {chartData.length > 0 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('Detailed breakdown')}</h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('In-depth organization analytics')}</p>
                    </div>
                    <EnhancedAnalyticsCharts chartsData={chartData} isLoading={false} colorTheme={colorTheme} chartTypeOverride={chartType} />
                  </div>
                )}
                {analyticsSummary.length === 0 && shortChartData.length === 0 && barChartData.length === 0 && chartData.length === 0 && (
                  <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <p className="text-sm">{t('No executive data available for this period.')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      </div>

      <CreateReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onGenerate={handleGenerateReport}
        activeTab={activeTab}
        currentTimeRange={selectedTimeRange}
        currentFilters={selectedFilters}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        filterOptions={filterOptions}
      />

      <AnalyticsHelpModal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        isOrgAdmin={!!userInfo?.is_org_admin}
      />
    </div>
  );
};

export default EnhancedReportsPage;